import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Route, CreateRouteForm, UpdateRouteForm } from '../types';

export class RouteService {
  // Route Management
  static async getRoutes(limitCount: number = 50, startAfterDate?: Date): Promise<Route[]> {
    try {
      const routesRef = collection(db, 'routes');
      const constraints: any[] = [orderBy('createdAt', 'desc'), limit(limitCount)];
      if (startAfterDate) {
        constraints.push(startAfter(startAfterDate));
      }
      const q = query(routesRef, ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          fromCity: data.fromCity,
          toCity: data.toCity,
          departureTime: data.departureTime, // HH:mm string
          arrivalTime: data.arrivalTime, // HH:mm string
          price: data.price,
          distance: data.distance || 0,
          duration: data.duration || 0,
          schedule: data.schedule || { isRecurring: false, days: [] },
          stops: data.stops || [],
          rates: data.rates || { luggage: 0, bag: 0 },
          isActive: data.isActive ?? true,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Route;
      });
    } catch (error) {
      console.error('Error fetching routes:', error);
      throw new Error('Failed to fetch routes');
    }
  }

  static async getRouteById(id: string): Promise<Route | null> {
    try {
      const routeRef = doc(db, 'routes', id);
      const routeSnap = await getDoc(routeRef);

      if (!routeSnap.exists()) {
        return null;
      }

      const data = routeSnap.data();
      return {
        id: routeSnap.id,
        fromCity: data.fromCity,
        toCity: data.toCity,
        departureTime: data.departureTime,
        arrivalTime: data.arrivalTime,
        price: data.price,
        distance: data.distance || 0,
        duration: data.duration || 0,
        schedule: data.schedule || { isRecurring: false, days: [] },
        stops: data.stops || [],
        rates: data.rates || { luggage: 0, bag: 0 },
        isActive: data.isActive ?? true,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Route;
    } catch (error) {
      console.error('Error fetching route:', error);
      throw new Error('Failed to fetch route');
    }
  }

  static async createRoute(routeData: CreateRouteForm): Promise<string> {
    try {
      const routesRef = collection(db, 'routes');
      const newRoute = {
        ...routeData,
        // Ensure complex objects are plain objects for Firestore
        schedule: {
          ...routeData.schedule,
          startDate: routeData.schedule.startDate ? Timestamp.fromDate(routeData.schedule.startDate) : null,
          endDate: routeData.schedule.endDate ? Timestamp.fromDate(routeData.schedule.endDate) : null,
        },
        stops: routeData.stops.map(stop => ({ ...stop })), // Ensure no custom prototype
        rates: { ...routeData.rates },
        isActive: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(routesRef, newRoute);
      return docRef.id;
    } catch (error: any) {
      console.error('Error creating route:', error);
      throw new Error(error.message || 'Failed to create route');
    }
  }

  static async updateRoute(id: string, routeData: UpdateRouteForm): Promise<void> {
    try {
      const routeRef = doc(db, 'routes', id);
      const updateData: any = {
        ...routeData,
        schedule: {
          ...routeData.schedule,
          startDate: routeData.schedule.startDate ? Timestamp.fromDate(routeData.schedule.startDate) : null,
          endDate: routeData.schedule.endDate ? Timestamp.fromDate(routeData.schedule.endDate) : null,
        },
        stops: routeData.stops.map(stop => ({ ...stop })),
        rates: { ...routeData.rates },
        updatedAt: Timestamp.now(),
      };

      await updateDoc(routeRef, updateData);
    } catch (error: any) {
      console.error('Error updating route:', error);
      throw new Error(error.message || 'Failed to update route');
    }
  }

  static async deleteRoute(id: string): Promise<void> {
    try {
      const routeRef = doc(db, 'routes', id);
      await deleteDoc(routeRef);
    } catch (error: any) {
      console.error('Error deleting route:', error);
      throw new Error(error.message || 'Failed to delete route');
    }
  }

  // Get routes by city pair
  static async getRoutesByCities(fromCity: string, toCity: string): Promise<Route[]> {
    try {
      const routesRef = collection(db, 'routes');
      const q = query(
        routesRef,
        where('fromCity', '==', fromCity),
        where('toCity', '==', toCity),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          fromCity: data.fromCity,
          toCity: data.toCity,
          departureTime: data.departureTime,
          arrivalTime: data.arrivalTime,
          price: data.price,
          distance: data.distance || 0,
          duration: data.duration || 0,
          schedule: data.schedule || { isRecurring: false, days: [] },
          stops: data.stops || [],
          rates: data.rates || { luggage: 0, bag: 0 },
          isActive: data.isActive ?? true,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Route;
      });
    } catch (error) {
      console.error('Error fetching routes by cities:', error);
      throw new Error('Failed to fetch routes');
    }
  }

  // Get active routes
  static async getActiveRoutes(): Promise<Route[]> {
    try {
      const routesRef = collection(db, 'routes');
      const q = query(
        routesRef,
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          fromCity: data.fromCity,
          toCity: data.toCity,
          departureTime: data.departureTime,
          arrivalTime: data.arrivalTime,
          price: data.price,
          distance: data.distance || 0,
          duration: data.duration || 0,
          schedule: data.schedule || { isRecurring: false, days: [] },
          stops: data.stops || [],
          rates: data.rates || { luggage: 0, bag: 0 },
          isActive: data.isActive ?? true,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Route;
      });
    } catch (error) {
      console.error('Error fetching active routes:', error);
      throw new Error('Failed to fetch active routes');
    }
  }
}
